import { Component, OnInit } from '@angular/core';
import { FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { firstValueFrom } from 'rxjs';
import { NotificationService } from 'src/app/services/notification.service';
import { Organization } from 'src/typings';
import { CreateOrgBankAccountDto, OrgBankAccount, TaxationType, UpdateOrgDto } from '../org.dto';
import { OrgService } from '../org.service';

@Component({
  selector: 'app-create',
  templateUrl: './create.component.html',
  styleUrls: ['./create.component.scss']
})
export class CreateComponent implements OnInit {
  loading = false;

  accountsLoading = false;

  saving = false;

  taxationTypes = Object.values(TaxationType);

  orgId?: number;

  private readonly ibanPattern = /^UA\d{27}$/i;

  private initialAccountIds: number[] = [];

  createForm: FormGroup = this.fb.group({
    name: ['', [Validators.required]],
    fullName: [''],
    edrpou: ['', [Validators.minLength(8), Validators.maxLength(10)]],
    ipn: ['', [Validators.minLength(10), Validators.maxLength(12)]],
    taxationType: [TaxationType.SIMPLIFIED_3, [Validators.required]],
    legalAddress: [''],
    actualAddress: [''],
    phone: [''],
    email: ['', [Validators.email]],
    shipmentSignatoryName: ['', [Validators.maxLength(200)]],
    shipmentSignatoryPosition: ['', [Validators.maxLength(120)]],
    director_id: [null],
    accountant_id: [null],
    isActive: [true]
  });

  accountsForm: FormArray = this.fb.array([]);

  constructor(
    private fb: FormBuilder,
    private orgService: OrgService,
    private router: Router,
    private route: ActivatedRoute,
    private notificationService: NotificationService
  ) {}

  async ngOnInit() {
    const id = this.route.snapshot.paramMap.get('id');
    if (id) {
      this.orgId = Number(id);
      await this.loadOrganization();
      await this.loadBankAccounts();
    }
  }

  private createBankAccountForm(account?: Partial<OrgBankAccount>) {
    return this.fb.group({
      id: [account?.id],
      iban: [account?.iban || '', [Validators.required, Validators.pattern(this.ibanPattern)]],
      bankName: [account?.bankName || ''],
      accountName: [account?.accountName || ''],
      isPrimary: [Boolean(account?.isPrimary)],
      isActive: [account?.isActive ?? true]
    });
  }

  private normalizeIban(iban?: string) {
    return iban?.trim().toUpperCase();
  }

  private hasAnyBankAccountValue(value: any): boolean {
    return Boolean(
      value?.id ||
        value?.iban?.trim() ||
        value?.bankName?.trim() ||
        value?.accountName?.trim() ||
        value?.isPrimary
    );
  }

  private sanitizeNullableNumber(value: unknown): number | null {
    if (value === '' || value === undefined || value === null) return null;
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  }

  private async loadOrganization() {
    if (!this.orgId) return;

    this.loading = true;
    try {
      const org = await firstValueFrom(this.orgService.getOne(this.orgId));
      this.createForm.patchValue({
        name: org.name,
        fullName: org.fullName || '',
        edrpou: org.edrpou || '',
        ipn: org.ipn || '',
        taxationType: org.taxationType || TaxationType.SIMPLIFIED_3,
        legalAddress: org.legalAddress || '',
        actualAddress: org.actualAddress || '',
        phone: org.phone || '',
        email: org.email || '',
        shipmentSignatoryName: org.shipmentSignatoryName || '',
        shipmentSignatoryPosition: org.shipmentSignatoryPosition || '',
        director_id: org.director?.id || null,
        accountant_id: org.accountant?.id || null,
        isActive: org.isActive !== undefined ? org.isActive : true
      });
    } catch (err: any) {
      console.error(err);
      this.notificationService.warning(err?.error?.message || 'Ошибка при загрузке организации');
      this.router.navigateByUrl('/org/list');
    } finally {
      this.loading = false;
    }
  }

  private async loadBankAccounts() {
    if (!this.orgId) return;

    this.accountsLoading = true;
    try {
      const accounts = await firstValueFrom(this.orgService.getBankAccounts(this.orgId));
      this.accountsForm.clear();
      this.initialAccountIds = (accounts || []).map((account) => account.id);
      accounts.forEach((account) => this.accountsForm.push(this.createBankAccountForm(account)));
    } catch (err: any) {
      console.error(err);
      this.notificationService.warning(err?.error?.message || 'Ошибка при загрузке счетов');
    } finally {
      this.accountsLoading = false;
    }
  }

  get isEditMode(): boolean {
    return !!this.orgId;
  }

  get pageTitle(): string {
    return this.isEditMode ? 'Редактирование организации' : 'Создание организации';
  }

  get bankAccountForms() {
    return this.accountsForm.controls as FormGroup[];
  }

  addBankAccountRow() {
    this.accountsForm.push(this.createBankAccountForm());
  }

  removeBankAccountRow(index: number) {
    this.accountsForm.removeAt(index);
  }

  setPrimary(index: number, checked: boolean) {
    this.bankAccountForms.forEach((ctrl, rowIndex) => {
      ctrl.patchValue({ isPrimary: rowIndex === index ? checked : false }, { emitEvent: false });
    });
  }

  copyLegalAddressToActual() {
    this.createForm.patchValue({ actualAddress: this.createForm.value.legalAddress || '' });
  }

  private validateAccounts(): boolean {
    const rows = this.bankAccountForms.filter((control) => this.hasAnyBankAccountValue(control.value));
    if (!rows.length) return true;

    let hasInvalid = false;
    rows.forEach((control) => {
      control.markAllAsTouched();
      if (control.invalid) hasInvalid = true;
    });

    if (hasInvalid) {
      this.notificationService.warning('Проверьте корректность банковских счетов (IBAN: UA + 27 цифр)');
      return false;
    }

    const primaryCount = rows.filter((control) => control.value?.isPrimary).length;
    if (primaryCount !== 1) {
      this.notificationService.warning('Для счетов организации должен быть выбран один основной счет');
      return false;
    }

    const normalizedIbans = rows.map((control) => this.normalizeIban(control.value?.iban));
    const unique = new Set(normalizedIbans);
    if (unique.size !== normalizedIbans.length) {
      this.notificationService.warning('IBAN счетов должны быть уникальными');
      return false;
    }

    return true;
  }

  private async syncBankAccounts(orgId: number) {
    const rows = this.bankAccountForms
      .map((control) => ({ ...control.value, iban: this.normalizeIban(control.value.iban) }))
      .filter((value) => this.hasAnyBankAccountValue(value));

    const existingIds = rows.filter((row) => row.id).map((row) => Number(row.id));
    const idsToDelete = this.initialAccountIds.filter((id) => !existingIds.includes(id));

    for (const id of idsToDelete) {
      await firstValueFrom(this.orgService.deleteBankAccount(id));
    }

    for (const row of rows) {
      const payloadBase = {
        org_id: orgId,
        iban: row.iban,
        bankName: row.bankName?.trim() || undefined,
        accountName: row.accountName?.trim() || undefined,
        isPrimary: Boolean(row.isPrimary),
        isActive: row.isActive !== undefined ? Boolean(row.isActive) : true
      };

      if (row.id) {
        await firstValueFrom(
          this.orgService.updateBankAccount({
            id: Number(row.id),
            ...payloadBase
          })
        );
      } else {
        await firstValueFrom(this.orgService.createBankAccount(payloadBase as CreateOrgBankAccountDto));
      }
    }
  }

  private getOrganizationPayload() {
    const formValue = this.createForm.value;

    return {
      name: formValue.name?.trim(),
      fullName: formValue.fullName?.trim() || undefined,
      edrpou: formValue.edrpou?.trim() || undefined,
      ipn: formValue.ipn?.trim() || undefined,
      taxationType: formValue.taxationType,
      legalAddress: formValue.legalAddress?.trim() || undefined,
      actualAddress: formValue.actualAddress?.trim() || undefined,
      phone: formValue.phone?.trim() || undefined,
      email: formValue.email?.trim() || undefined,
      shipmentSignatoryName: formValue.shipmentSignatoryName?.trim() || null,
      shipmentSignatoryPosition: formValue.shipmentSignatoryPosition?.trim() || null,
      director_id: this.sanitizeNullableNumber(formValue.director_id),
      accountant_id: this.sanitizeNullableNumber(formValue.accountant_id),
      isActive: Boolean(formValue.isActive)
    };
  }

  async submit() {
    this.createForm.markAllAsTouched();
    if (this.createForm.invalid) {
      this.notificationService.warning('Проверьте корректность формы организации');
      return;
    }

    if (!this.validateAccounts()) return;

    this.saving = true;
    try {
      const payload = this.getOrganizationPayload();
      let orgId = this.orgId;

      if (this.isEditMode) {
        const updatePayload: UpdateOrgDto = {
          id: Number(this.orgId),
          ...payload
        };
        await firstValueFrom(this.orgService.update(updatePayload));
      } else {
        const created = await firstValueFrom(this.orgService.create(payload));
        orgId = created.id;
        this.orgId = created.id;
      }

      if (orgId) {
        await this.syncBankAccounts(orgId);
      }

      this.notificationService.notice(this.isEditMode ? 'Организация обновлена' : 'Организация создана');
      this.router.navigateByUrl('/org/list');
    } catch (err: any) {
      console.error(err);
      this.notificationService.warning(
        err?.error?.message ||
          (this.isEditMode ? 'Ошибка при обновлении организации' : 'Ошибка при создании организации')
      );
    } finally {
      this.saving = false;
    }
  }
}
